import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase.ts';
import { Wizard } from '../components/Wizard.tsx';
import { input as inputCls } from '../lib/ui.ts';
import type { Build, Client, Role } from '../lib/types.ts';

/**
 * The "Design" experience: a client picker + the guided Wizard + that client's
 * builds. Reused by both roles — RLS scopes the client list (admins see all,
 * designers see assigned). Server-side fences (is_live 403, write allow-list,
 * anti-cascade) apply regardless of role, so admin building never bypasses them.
 */
export function DesignerDashboard({ viewerRole }: { viewerRole?: Role } = {}) {
  const [clients, setClients] = useState<Client[]>([]);
  // Restore the last-active client so a reload returns you to the same one (not
  // always the first). Validated against the loaded list below in case it's gone.
  const [clientId, setClientId] = useState<string>(() => localStorage.getItem('alchmy.clientId') ?? '');
  const [builds, setBuilds] = useState<Build[]>([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (clientId) localStorage.setItem('alchmy.clientId', clientId);
  }, [clientId]);

  function reloadClients() {
    void supabase
      .from('clients')
      .select('*')
      .order('name')
      .then(({ data }) => {
        const list = (data ?? []) as Client[];
        setClients(list);
        // Keep the restored/selected client if it still exists; else fall to first.
        setClientId((prev) => (prev && list.some((c) => c.id === prev) ? prev : (list[0]?.id ?? '')));
      });
  }

  useEffect(() => {
    reloadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!clientId) return;
    void supabase
      .from('builds')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false })
      .limit(100)
      .then(({ data }) => setBuilds((data ?? []) as Build[]));
  }, [clientId, tick]);

  const selected = useMemo(() => clients.find((c) => c.id === clientId), [clients, clientId]);

  // Reload both the client row (for fresh wizard_state) and its builds.
  function onActivity() {
    reloadClients();
    setTick((t) => t + 1);
  }

  if (clients.length === 0) {
    return (
      <p className="text-sm text-ink-2">
        {viewerRole === 'admin'
          ? 'No clients yet. Create one in the Admin tab, then build it here.'
          : 'You have no assigned clients yet. Ask Neil to assign you.'}
      </p>
    );
  }

  return (
    <div className="space-y-8">
      <section className="flex items-center gap-3">
        <label className="text-sm font-medium text-ink-2">Client</label>
        <select
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          className={inputCls}
        >
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.is_live ? '🔒 ' : ''}{c.name}
            </option>
          ))}
        </select>
        <span className="text-xs text-ink-2">
          {viewerRole === 'admin' && <>branch: app/{selected?.slug}</>}
          {!selected?.pages_project && (
            <>{viewerRole === 'admin' ? ' · ' : ''}Preview links aren't set up yet</>
          )}
        </span>
      </section>

      {selected && <Wizard client={selected} builds={builds} onActivity={onActivity} isAdmin={viewerRole === 'admin'} />}
      {/* E: recent-builds history removed — builds are now done in the Claude Code hand-build
          session (build-model.md), so the engine's build history is stale here. `builds` is still
          fetched + passed to the Wizard (the preview/go-live steps read it). */}
    </div>
  );
}
